import { useState } from 'react';
import { apiFetch } from '../lib/api.js';
import { C, alpha } from '../lib/theme.js';
import { Button, Card } from '../ui/ui.js';

interface OrphanCampaign {
  id: string;
  name: string;
  status: string;
  created_time: string | null;
  basil_named: boolean;
  tracked: boolean;
  recommended: boolean;
}

/**
 * Tidy up Meta Ads Manager: scan the account for non-live campaigns (leftovers
 * from failed builds, plus any other paused/inactive campaigns) and delete the
 * chosen ones from Meta. An ACTIVE / delivering campaign is never listed, and the
 * server re-checks status before deleting, so a live campaign can't be removed.
 */
export function MetaCleanup({ orgId }: { orgId: string }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<OrphanCampaign[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function scan() {
    setOpen(true);
    setLoading(true);
    setError(null);
    setNote(null);
    try {
      const d = await apiFetch<{ campaigns: OrphanCampaign[] }>(
        `/api/orgs/${orgId}/campaigns/meta-orphans`,
      );
      setRows(d.campaigns);
      setSelected(new Set(d.campaigns.filter((c) => c.recommended).map((c) => c.id)));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Scan failed');
    } finally {
      setLoading(false);
    }
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function del() {
    setBusy(true);
    setError(null);
    try {
      const d = await apiFetch<{ deleted: string[]; skipped: string[] }>(
        `/api/orgs/${orgId}/campaigns/meta-orphans/delete`,
        { method: 'POST', body: JSON.stringify({ ids: Array.from(selected) }) },
      );
      const skipped = d.skipped.length
        ? `, ${d.skipped.length} skipped (still active or gone)`
        : '';
      setNote(`Removed ${d.deleted.length} from Meta${skipped}.`);
      setConfirm(false);
      await scan();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setBusy(false);
    }
  }

  const statusColor = (s: string) => (s === 'ACTIVE' ? C.good : s === 'PAUSED' ? C.watch : C.muted);

  return (
    <Card style={{ padding: '16px 18px', marginTop: 18 }}>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.primary }}>
            Tidy up Meta Ads Manager
          </div>
          <div style={{ fontSize: 12.5, color: C.muted, marginTop: 3, lineHeight: 1.5 }}>
            Find non-live campaigns left in your Meta account (including leftovers from failed
            builds) and delete the ones you don&apos;t need. Live, delivering campaigns are never
            touched.
          </div>
        </div>
        {!open && (
          <Button variant="primary" onClick={() => void scan()} style={{ whiteSpace: 'nowrap' }}>
            Scan Meta
          </Button>
        )}
      </div>

      {open && (
        <div style={{ marginTop: 14 }}>
          {loading && (
            <div style={{ color: C.muted, font: `500 12px 'IBM Plex Mono',monospace` }}>
              SCANNING META…
            </div>
          )}

          {!loading && rows && rows.length === 0 && (
            <div style={{ fontSize: 13, color: C.muted }}>
              Nothing to clean. No non-live campaigns are sitting in your Meta account.
            </div>
          )}

          {!loading && rows && rows.length > 0 && (
            <>
              <div
                style={{
                  font: `500 10px 'IBM Plex Mono',monospace`,
                  color: C.faint,
                  marginBottom: 8,
                }}
              >
                {rows.length} NON-LIVE CAMPAIGN{rows.length === 1 ? '' : 'S'} · {selected.size}{' '}
                SELECTED
              </div>
              <div style={{ border: `1px solid ${C.line}`, borderRadius: 10, overflow: 'hidden' }}>
                {rows.map((r, i) => (
                  <label
                    key={r.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 11,
                      padding: '10px 13px',
                      borderTop: i === 0 ? 'none' : `1px solid ${C.lineSoft}`,
                      cursor: 'pointer',
                      background: selected.has(r.id) ? alpha(C.act, 7) : 'transparent',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => toggle(r.id)}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: C.primary,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {r.name}
                      </div>
                      <div
                        style={{
                          font: `500 10px 'IBM Plex Mono',monospace`,
                          color: C.faint,
                          marginTop: 3,
                        }}
                      >
                        {r.created_time ? r.created_time.slice(0, 10) : 'no date'}
                        {r.basil_named ? ' · BASIL-MADE' : ' · NOT BASIL'}
                        {r.recommended ? ' · LEFTOVER' : ''}
                      </div>
                    </div>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 999,
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: '.03em',
                        color: statusColor(r.status),
                        background: alpha(statusColor(r.status), 12),
                        border: `1px solid ${alpha(statusColor(r.status), 35)}`,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.status}
                    </span>
                  </label>
                ))}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 12 }}>
                {confirm ? (
                  <>
                    <span style={{ fontSize: 12.5, color: C.act, fontWeight: 600 }}>
                      Delete {selected.size} from Meta? This can&apos;t be undone.
                    </span>
                    <Button variant="primary" onClick={() => void del()} disabled={busy}>
                      {busy ? 'Deleting…' : 'Yes, delete'}
                    </Button>
                    <span
                      onClick={() => setConfirm(false)}
                      style={{ fontSize: 12.5, color: C.muted, cursor: 'pointer' }}
                    >
                      Cancel
                    </span>
                  </>
                ) : (
                  <>
                    <Button
                      variant="primary"
                      onClick={() => selected.size > 0 && setConfirm(true)}
                      disabled={selected.size === 0}
                    >
                      Delete {selected.size} from Meta
                    </Button>
                    <span
                      onClick={() => void scan()}
                      style={{ fontSize: 12, color: C.basil, cursor: 'pointer' }}
                    >
                      Rescan
                    </span>
                  </>
                )}
              </div>
            </>
          )}

          {note && <div style={{ fontSize: 12.5, color: C.good, marginTop: 10 }}>{note}</div>}
          {error && <div style={{ fontSize: 12.5, color: C.act, marginTop: 10 }}>{error}</div>}
        </div>
      )}
    </Card>
  );
}
